import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Car, User, Mail, Phone, Calendar, DollarSign, Camera, CheckCircle, AlertCircle, Gauge, Palette, Settings, Fuel } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Common vehicle makes
const MAKES = [
  'Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 'Dodge', 
  'Ford', 'GMC', 'Honda', 'Hyundai', 'Infiniti', 'Jeep', 'Kia', 'Lexus', 
  'Lincoln', 'Mazda', 'Mercedes-Benz', 'Mitsubishi', 'Nissan', 'Porsche', 
  'Ram', 'Subaru', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo', 'Autre'
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 30 }, (_, i) => CURRENT_YEAR - i);

export default function SellVehiclePage() {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [formData, setFormData] = useState({
    // Contact
    name: '',
    email: '',
    phone: '',
    
    // Vehicle
    year: '',
    make: '',
    model: '',
    trim: '',
    mileage: '',
    vin: '',
    exterior_color: '',
    interior_color: '',
    transmission: 'automatic',
    drivetrain: 'fwd',
    fuel_type: 'gasoline',
    
    // Condition
    condition: '',
    accident_history: 'none',
    mechanical_issues: '',
    
    // Intent
    intent: 'sell',
    desired_price: '',
    timeline: 'flexible',
    
    // Photos
    photos: [],
    
    // Notes
    additional_notes: ''
  });

  const CONDITIONS = [
    { value: 'excellent', label: t('sell.excellent'), desc: t('sell.excellentDesc') },
    { value: 'good', label: t('sell.good'), desc: t('sell.goodDesc') },
    { value: 'fair', label: t('sell.fair'), desc: t('sell.fairDesc') },
    { value: 'poor', label: t('sell.poor'), desc: t('sell.poorDesc') }
  ];

  const ACCIDENT_OPTIONS = [
    { value: 'none', label: t('sell.noAccident') },
    { value: 'minor', label: t('sell.minorAccident') },
    { value: 'major', label: t('sell.majorAccident') }
  ];

  const TIMELINES = [
    { value: 'immediate', label: t('sell.immediate') },
    { value: '1-2_weeks', label: t('sell.oneToTwoWeeks') },
    { value: '1_month', label: t('sell.oneMonth') },
    { value: 'flexible', label: t('sell.flexible') }
  ];

  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('sell.nameRequired');
    if (!formData.email.trim()) newErrors.email = t('sell.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t('sell.emailInvalid');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.year) newErrors.year = t('sell.yearRequired');
    if (!formData.make) newErrors.make = t('sell.makeRequired');
    if (!formData.model.trim()) newErrors.model = t('sell.modelRequired');
    if (!formData.mileage) newErrors.mileage = t('sell.mileageRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.condition) newErrors.condition = t('sell.conditionRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3 && validateStep3()) setStep(4);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        year: parseInt(formData.year),
        mileage: parseInt(formData.mileage),
        desired_price: formData.desired_price ? parseFloat(formData.desired_price) : null
      };
      
      const response = await fetch(`${API_URL}/api/appraisals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Submit error');
      }
      
      setIsSubmitted(true);
      toast.success(t('sell.submitSuccess'));
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(t('sell.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-20">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4">{t('sell.successTitle')}</h1>
            <p className="text-slate-600 mb-2">
              {t('sell.successThank')} <strong>{formData.name}</strong> {t('sell.successDesc')}
            </p>
            <p className="text-slate-600 mb-8">
              {t('sell.successReview')} <strong>{formData.year} {formData.make} {formData.model}</strong> {t('sell.successContact')} <strong>{formData.email}</strong>.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/">
                <Button variant="outline">{t('sell.backHome')}</Button>
              </Link>
              <Link to="/inventory">
                <Button className="bg-blue-600 hover:bg-blue-700">{t('sell.seeInventory')}</Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900">
      <Navbar />
      
      {/* Hero Section with Car Background */}
      <div className="relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&h=600&fit=crop"
            alt="Luxury car"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-900 via-zinc-900/90 to-zinc-900/70"></div>
        </div>
        
        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
              <span className="text-blue-400 text-sm font-medium">{t('sell.heroTag')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('sell.heroTitle')} <span className="text-blue-500">{t('sell.heroTitleHighlight')}</span>
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              {t('sell.heroDesc')}
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6 mb-8">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
                <span className="text-white text-sm font-medium">{t('sell.securePayment')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-blue-400" />
                </div>
                <span className="text-white text-sm font-medium">{t('sell.bestPrices')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <Car className="h-5 w-5 text-purple-400" />
                </div>
                <span className="text-white text-sm font-medium">{t('sell.allBrands')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 -mt-8 relative z-10">
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: t('sell.stepContact') },
              { num: 2, label: t('sell.stepVehicle') },
              { num: 3, label: t('sell.stepCondition') },
              { num: 4, label: t('sell.stepConfirmation') }
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-colors ${
                  step >= s.num ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > s.num ? <CheckCircle className="h-5 w-5" /> : s.num}
                </div>
                <span className={`ml-2 text-sm font-medium hidden sm:inline ${step >= s.num ? 'text-blue-600' : 'text-slate-400'}`}>
                  {s.label}
                </span>
                {i < 3 && <div className={`w-12 sm:w-24 h-1 mx-2 rounded ${step > s.num ? 'bg-blue-600' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 pb-16">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          
          {/* Step 1: Contact Information */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">{t('sell.yourInfo')}</h2>
                  <p className="text-slate-500">{t('sell.yourInfoDesc')}</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-slate-700">{t('sell.fullName')} *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="name"
                      placeholder="Jean Dupont"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      className={`pl-10 ${errors.name ? 'border-red-500' : ''}`}
                      data-testid="appraisal-name"
                    />
                  </div>
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-slate-700">{t('sell.email')} *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="jean@exemple.com"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="appraisal-email"
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                
                <div className="md:col-span-2">
                  <Label htmlFor="phone" className="text-slate-700">{t('sell.phoneOptional')}</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(514) 555-1234"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      className="pl-10"
                      data-testid="appraisal-phone"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Vehicle Information */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Car className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">{t('sell.vehicleInfo')}</h2>
                  <p className="text-slate-500">{t('sell.vehicleInfoDesc')}</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-700">{t('sell.year')} *</Label>
                  <Select value={formData.year} onValueChange={(v) => updateField('year', v)}>
                    <SelectTrigger className={errors.year ? 'border-red-500' : ''} data-testid="appraisal-year">
                      <SelectValue placeholder={t('sell.year')} />
                    </SelectTrigger>
                    <SelectContent>
                      {YEARS.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.year && <p className="text-red-500 text-sm mt-1">{errors.year}</p>}
                </div>
                
                <div>
                  <Label className="text-slate-700">{t('sell.make')} *</Label>
                  <Select value={formData.make} onValueChange={(v) => updateField('make', v)}>
                    <SelectTrigger className={errors.make ? 'border-red-500' : ''} data-testid="appraisal-make">
                      <SelectValue placeholder={t('sell.make')} />
                    </SelectTrigger>
                    <SelectContent>
                      {MAKES.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.make && <p className="text-red-500 text-sm mt-1">{errors.make}</p>}
                </div>
                
                <div>
                  <Label className="text-slate-700">{t('sell.model')} *</Label>
                  <Input
                    placeholder="Ex: Civic, Corolla"
                    value={formData.model}
                    onChange={(e) => updateField('model', e.target.value)}
                    className={errors.model ? 'border-red-500' : ''}
                    data-testid="appraisal-model"
                  />
                  {errors.model && <p className="text-red-500 text-sm mt-1">{errors.model}</p>}
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">{t('sell.trimOptional')}</Label>
                  <Input
                    placeholder="Ex: LX, Sport, Limited"
                    value={formData.trim}
                    onChange={(e) => updateField('trim', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label className="text-slate-700">{t('sell.mileage')} *</Label>
                  <div className="relative">
                    <Gauge className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="number"
                      placeholder="Ex: 85000"
                      value={formData.mileage}
                      onChange={(e) => updateField('mileage', e.target.value)}
                      className={`pl-10 ${errors.mileage ? 'border-red-500' : ''}`}
                      data-testid="appraisal-mileage"
                    />
                  </div>
                  {errors.mileage && <p className="text-red-500 text-sm mt-1">{errors.mileage}</p>}
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">{t('sell.vinOptional')}</Label>
                  <Input
                    placeholder={t('sell.vinChars')}
                    value={formData.vin}
                    onChange={(e) => updateField('vin', e.target.value.toUpperCase())}
                    maxLength={17}
                  />
                </div>
                
                <div>
                  <Label className="text-slate-700">{t('sell.exteriorColor')}</Label>
                  <div className="relative">
                    <Palette className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Ex: Noir, Blanc, Gris"
                      value={formData.exterior_color}
                      onChange={(e) => updateField('exterior_color', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-700">{t('sell.transmission')}</Label>
                  <Select value={formData.transmission} onValueChange={(v) => updateField('transmission', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="automatic">{t('sell.automatic')}</SelectItem>
                      <SelectItem value="manual">{t('sell.manual')}</SelectItem>
                      <SelectItem value="cvt">{t('sell.cvt')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-700">{t('sell.drivetrain')}</Label>
                  <Select value={formData.drivetrain} onValueChange={(v) => updateField('drivetrain', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fwd">{t('sell.fwdLabel')}</SelectItem>
                      <SelectItem value="rwd">{t('sell.rwdLabel')}</SelectItem>
                      <SelectItem value="awd">{t('sell.awdLabel')}</SelectItem>
                      <SelectItem value="4wd">{t('sell.fourWdLabel')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-700">{t('sell.fuelType')}</Label>
                  <Select value={formData.fuel_type} onValueChange={(v) => updateField('fuel_type', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gasoline">{t('sell.gasoline')}</SelectItem>
                      <SelectItem value="diesel">{t('sell.diesel')}</SelectItem>
                      <SelectItem value="hybrid">{t('sell.hybrid')}</SelectItem>
                      <SelectItem value="electric">{t('sell.electric')}</SelectItem>
                      <SelectItem value="plugin_hybrid">{t('sell.pluginHybrid')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Condition & Intent */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Settings className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">{t('sell.conditionTitle')}</h2>
                  <p className="text-slate-500">{t('sell.conditionDesc')}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700 mb-3 block">{t('sell.generalCondition')} *</Label>
                <div className="grid md:grid-cols-2 gap-3">
                  {CONDITIONS.map(c => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => updateField('condition', c.value)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        formData.condition === c.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`condition-${c.value}`}
                    >
                      <p className="font-semibold text-slate-800">{c.label}</p>
                      <p className="text-sm text-slate-500">{c.desc}</p>
                    </button>
                  ))}
                </div>
                {errors.condition && <p className="text-red-500 text-sm mt-2">{errors.condition}</p>}
              </div>
              
              <div>
                <Label className="text-slate-700 mb-3 block">{t('sell.accidentHistory')}</Label>
                <div className="grid md:grid-cols-3 gap-3">
                  {ACCIDENT_OPTIONS.map(a => (
                    <button
                      key={a.value}
                      type="button"
                      onClick={() => updateField('accident_history', a.value)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        formData.accident_history === a.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className="text-sm font-medium text-slate-800">{a.label}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700">{t('sell.mechanicalIssues')}</Label>
                <Textarea
                  placeholder={t('sell.mechanicalPlaceholder')}
                  value={formData.mechanical_issues}
                  onChange={(e) => updateField('mechanical_issues', e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              <div className="border-t pt-6">
                <Label className="text-slate-700 mb-3 block">{t('sell.yourIntent')}</Label>
                <div className="grid md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => updateField('intent', 'sell')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.intent === 'sell'
                        ? 'border-green-500 bg-green-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="font-semibold text-slate-800">{t('sell.sellIntent')}</p>
                    <p className="text-sm text-slate-500">{t('sell.sellIntentDesc')}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => updateField('intent', 'trade-in')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.intent === 'trade-in'
                        ? 'border-green-500 bg-green-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="font-semibold text-slate-800">{t('sell.tradeIntent')}</p>
                    <p className="text-sm text-slate-500">{t('sell.tradeIntentDesc')}</p>
                  </button>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">{t('sell.desiredPrice')}</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="number"
                      placeholder="Ex: 15000"
                      value={formData.desired_price}
                      onChange={(e) => updateField('desired_price', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-700">{t('sell.timeline')}</Label>
                  <Select value={formData.timeline} onValueChange={(v) => updateField('timeline', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMELINES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Submit */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">{t('sell.confirmTitle')}</h2>
                  <p className="text-slate-500">{t('sell.confirmDesc')}</p>
                </div>
              </div>
              
              {/* Summary Cards */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" /> {t('sell.contact')}
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-slate-500">{t('sell.fullName')}:</span> {formData.name}</p>
                    <p><span className="text-slate-500">{t('sell.email')}:</span> {formData.email}</p>
                    {formData.phone && <p><span className="text-slate-500">{t('sell.phoneOptional').replace(' (optionnel)', '').replace(' (optional)', '').replace(' (opcional)', '').replace(' (اختياري)', '')}:</span> {formData.phone}</p>}
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Car className="h-4 w-4" /> {t('sell.vehicle')}
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p className="font-medium text-blue-600">{formData.year} {formData.make} {formData.model} {formData.trim}</p>
                    <p><span className="text-slate-500">{t('sell.mileage')}:</span> {parseInt(formData.mileage).toLocaleString()} km</p>
                    {formData.vin && <p><span className="text-slate-500">VIN:</span> {formData.vin}</p>}
                    <p><span className="text-slate-500">{t('sell.transmission')}:</span> {formData.transmission === 'automatic' ? t('sell.automatic') : t('sell.manual')}</p>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" /> {t('sell.condition')}
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-slate-500">{t('sell.condition')}:</span> {CONDITIONS.find(c => c.value === formData.condition)?.label}</p>
                    <p><span className="text-slate-500">{t('sell.accidents')}:</span> {ACCIDENT_OPTIONS.find(a => a.value === formData.accident_history)?.label}</p>
                    {formData.mechanical_issues && <p><span className="text-slate-500">{t('sell.issues')}:</span> {formData.mechanical_issues}</p>}
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" /> {t('sell.intent')}
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-slate-500">{t('sell.type')}:</span> {formData.intent === 'sell' ? t('sell.sellIntent') : t('sell.tradeIntent')}</p>
                    {formData.desired_price && <p><span className="text-slate-500">{t('sell.desiredPrice').replace(' (optionnel)', '').replace(' (optional)', '').replace(' (opcional)', '').replace(' (اختياري)', '')}:</span> {parseInt(formData.desired_price).toLocaleString()} $</p>}
                    <p><span className="text-slate-500">{t('sell.timeline')}:</span> {TIMELINES.find(tl => tl.value === formData.timeline)?.label}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700">{t('sell.additionalNotes')}</Label>
                <Textarea
                  placeholder={t('sell.additionalPlaceholder')}
                  value={formData.additional_notes}
                  onChange={(e) => updateField('additional_notes', e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  <strong>{t('sell.nextSteps')}</strong> {t('sell.nextStepsDesc')}
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            {step > 1 ? (
              <Button variant="outline" onClick={prevStep}>
                {t('sell.previous')}
              </Button>
            ) : (
              <div />
            )}
            
            {step < 4 ? (
              <Button onClick={nextStep} className="bg-blue-600 hover:bg-blue-700">
                {t('sell.next')}
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="submit-appraisal"
              >
                {isSubmitting ? t('sell.submitting') : t('sell.submitRequest')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
